import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DEAL_STATUSES } from '@bcc/shared';
import { ROLES } from '@bcc/shared';
import { DEAL_ASSET_TYPE_OPTIONS, DEAL_PROPERTY_CLASS_OPTIONS, DEAL_STRATEGY_OPTIONS } from '../../constants/dealFormOptions';
import { SITE_FILE_CONTROL, SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export function UploadDeal() {
  const { session, role } = useAuth();
  const token = session?.access_token;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [propertyType, setPropertyType] = useState<string>(DEAL_ASSET_TYPE_OPTIONS[0]);
  const [propertyClass, setPropertyClass] = useState<string>(DEAL_PROPERTY_CLASS_OPTIONS[0]);
  const [strategy, setStrategy] = useState<string>(DEAL_STRATEGY_OPTIONS[0]);
  const [irr, setIrr] = useState('14.5');
  const [equityMultiple, setEquityMultiple] = useState('1.9');
  const [min, setMin] = useState('25000');
  const [status, setStatus] = useState<(typeof DEAL_STATUSES)[keyof typeof DEAL_STATUSES]>(DEAL_STATUSES.OPEN);
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (role !== ROLES.ADMIN) {
    return <Navigate to="/admin/crm" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    if (!file) {
      setErr('A cover image is required.');
      return;
    }
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `deals/${crypto.randomUUID()}.${ext}`;
      const up = await supabase.storage.from('deal-assets').upload(path, file, {
        upsert: false,
        contentType: file.type || 'image/jpeg',
      });
      if (up.error) throw new Error(up.error.message);

      await apiFetch('/api/deals', {
        method: 'POST',
        token,
        body: JSON.stringify({
          title: name.trim(),
          description: description.trim(),
          city: city.trim() || null,
          state: state.trim() || null,
          property_type: propertyType || null,
          property_class: propertyClass || null,
          strategy: strategy || null,
          irr: Number(irr),
          equity_multiple: Number(equityMultiple),
          min_investment: Number(min),
          status,
          image_paths: [path],
        }),
      });
      setDone(true);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Upload deal</h1>
        <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">
          Creates a public offering row. A cover image and geography tags power the Deals page cards.
        </p>
      </div>

      {done ? (
        <div className={`${SITE_GLASS_CARD} p-6 text-sm text-[var(--home-text-on-dark)]`}>
          <p>Deal published. View it on the public </p>
          <a href="/deals" className="font-medium text-[var(--home-miami-blue)] underline underline-offset-2">
            Deals
          </a>{' '}
          page (open status only).
        </div>
      ) : (
        <form className={`space-y-4 p-6 ${SITE_GLASS_CARD}`} onSubmit={onSubmit}>
          {err ? <p className="text-sm text-rose-300">{err}</p> : null}
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Deal name *</span>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Description *</span>
            <textarea
              required
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">City *</span>
              <input required value={city} onChange={(e) => setCity(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">State / region *</span>
              <input
                required
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="e.g. FL"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Property type *</span>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            >
              {DEAL_ASSET_TYPE_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Property class *</span>
            <select
              value={propertyClass}
              onChange={(e) => setPropertyClass(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            >
              {DEAL_PROPERTY_CLASS_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Strategy *</span>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              className={`w-full ${SITE_FORM_CONTROL}`}
            >
              {DEAL_STRATEGY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Cover image *</span>
            <input
              required
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className={`w-full text-sm text-[var(--home-muted-on-dark)] ${SITE_FILE_CONTROL}`}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">IRR %</span>
              <input value={irr} onChange={(e) => setIrr(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Equity multiple</span>
              <input
                value={equityMultiple}
                onChange={(e) => setEquityMultiple(e.target.value)}
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Minimum investment ($)</span>
            <input type="number" value={min} onChange={(e) => setMin(e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className={`w-full ${SITE_FORM_CONTROL}`}>
              {Object.values(DEAL_STATUSES).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="rounded-xl bg-[var(--home-miami-blue)] px-6 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] shadow-md hover:brightness-105"
          >
            Publish to Deals page
          </button>
        </form>
      )}
    </div>
  );
}
